import { HttpClient } from '@angular/common/http'
import { Injectable, inject } from '@angular/core'
import { Auth, signOut, User as FirebaseUser, signInWithEmailAndPassword } from '@angular/fire/auth'
import { Router } from '@angular/router'

import { finalize, from, Observable, retry, switchMap, tap, timeout } from 'rxjs'

import { User } from '@yoshu-lib/models/user.model'

import { TranslateService } from '@ngx-translate/core'

import { AUTH_STORE } from '../tokens/auth-store.token'
import { ENVIRONMENT } from '../tokens/environment.token'

/**
 * Authentication service handling login, logout, and token refresh.
 *
 * This service uses HttpOnly cookies for token storage (both access and refresh tokens).
 * The browser automatically sends cookies with requests when `withCredentials: true` is set.
 *
 * **Backend Cookie Requirements:**
 * The backend MUST set the following attributes on authentication cookies:
 * - `HttpOnly=true` - Prevents JavaScript access (XSS protection)
 * - `Secure=true` - Only sent over HTTPS
 * - `SameSite=Strict` or `SameSite=Lax` - CSRF protection
 * - `Path=/` - Available for all API routes
 *
 * Expected cookies set by backend:
 * - `access_token` - Short-lived JWT for API authentication
 * - `refresh_token` - Long-lived token for obtaining new access tokens
 */
@Injectable({ providedIn: 'root' })
export class AuthService {
  private env = inject(ENVIRONMENT)
  private auth = inject(Auth)
  private authStore = inject(AUTH_STORE)
  private router = inject(Router)
  private http = inject(HttpClient)
  private translate = inject(TranslateService)

  signInWithGoogle(email: string, password: string) {
    if (email.endsWith('@yoshu.ai')) {
      this.auth.tenantId = null
    } else this.auth.tenantId = this.env.tenantId
    this.authStore.setIsLoggingIn(true)

    return from(
      signInWithEmailAndPassword(this.auth, email, password).then((res) => res.user)
    ).pipe(finalize(() => this.authStore.setIsLoggingIn(false)))
  }

  loginToYoshuApi(firebaseUser: FirebaseUser) {
    return from(firebaseUser.getIdToken()).pipe(
      timeout(10000), // 10 second timeout for getting Firebase token
      switchMap((idToken) => {
        return this.http
          .post<User>(
            `${this.env.apiUrl}/auth/login/`,
            { firebase_id_token: idToken },
            { withCredentials: true } // Backend sets HttpOnly cookies for tokens
          )
          .pipe(
            timeout(10000), // 10 second timeout for API call
            retry({ count: 2, delay: 1000 }), // Retry twice with 1 second delay
            tap((user) => {
              this.authStore.setUser(user)

              const userLang = user.language?.split('-')[0]
              const currentLang = this.translate.getCurrentLang()

              if (userLang && userLang !== currentLang) {
                this.translate.use(userLang)
              }
            })
          )
      })
    )
  }

  logout(): Observable<void> {
    // Call backend to clear HttpOnly cookies, then sign out from Firebase
    return this.http
      .post<void>(`${this.env.apiUrl}/auth/logout/`, {}, { withCredentials: true })
      .pipe(
        switchMap(() => from(signOut(this.auth))),
        tap(() => {
          this.authStore.clearAuth()
          this.router.navigate(['/login'])
        })
      )
  }

  refreshAccessToken(): Observable<void> {
    // Refresh token is sent automatically via HttpOnly cookie
    // Backend validates it and sets new access_token cookie in response
    return this.http.post<void>(`${this.env.apiUrl}/auth/refresh/`, {}, { withCredentials: true })
  }
}
